import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '@env/environment';
import { BrandEnum } from '@infrastructure/brand.enum';
import { ModelReponseWithVariantsModelFromApi } from '@models/api-models/ModelReponseWithVariantsModelFromApi';
import { ComponentVariantFromApiModel } from '@models/api-models/component-variant-fromapi';
import { ModelModel } from '@models/api-models/model';
import { ComponentVariantModel } from '@models/component-variant';
import { ManualDefaultModel } from '@models/manual-default';
import { MiscModel } from '@models/misc-model';
import { ModelGroupModel } from '@models/model-group';
import { ModelSizeMinMaxModel } from '@models/model-size-min-max';
import { ProductLineModel } from '@models/product-line-model';
import { TagModel } from '@models/tag';
import { UserDataModel } from '@models/user-data-model';
import { BrandModeService } from '@services/brandmode.service';
import { EnduserDisplayService } from '@services/enduserdisplay.service';
import { HttpService } from '@services/http.service';
import { StoreService } from '@services/store.service';
import { UserService } from '@services/user.service';
import { PermissionConstants } from '@shared/permission-constants';
import { plainToClass } from 'class-transformer';
import { firstValueFrom, Observable } from 'rxjs';
import { ComponentVariantFromApiFactory } from '@factories/ComponentVariantFromApiFactory';
import { CustomiserApiService } from './customiser-api.service';
import { MiscModelsService } from './misc-models.service';

export interface ModelGroupState {
	modelGroups: ModelGroupModel[] | undefined;
}

@Injectable({
	providedIn: 'root'
})
export class ModelGroupStore {

	private http = inject(HttpClient);

	private state = signal<ModelGroupState>({
		modelGroups: undefined
	});

	public modelGroups = computed(async () => {
		if (!this.state().modelGroups || this.state().modelGroups?.length === 0) {
			let modelGroupsFromDb = await firstValueFrom(this.http.get<ModelGroupModel[]>(`${environment.dataUrl}/modelgroups`));

			this.setModelGroups(modelGroupsFromDb);
		}

		return this.state().modelGroups;
	});

	public setModelGroups(modelGroups: ModelGroupModel[]) {
		this.state.set({ ...this.state(), modelGroups: modelGroups });
	}
}

@Injectable({
	providedIn: 'root'
})
export class ModelsService {

	modelGroupStore = inject(ModelGroupStore);
	miscModelsService = inject(MiscModelsService);
	
	private modelDataUrl: string = `/models?requestModel.modelGroupUuid=`;
	private variantDataUrl: string = `/componentvariant?`;
	private modelWithAllDataUrl: string = `/allModelData/`;
	private stockKeepingUnitsEndpoint: string = 'StockKeepingUnits';
	private isUserInAdminMode: boolean;

	constructor(
		private customiserApi: CustomiserApiService,
		private brandModeService: BrandModeService,
		private userService: UserService,
		private storeService: StoreService,
		private enduserDisplayService: EnduserDisplayService,
		private httpService: HttpService,
		private http: HttpClient
	) {

		this.userService.adminModeEnabled.subscribe(x => {
			this.isUserInAdminMode = x;
		});
	}

	public async getModelsByLine(lineId: string) {
		return await this.customiserApi.get<ModelsResponse[]>('models/list?id=' + lineId);
	}

	private async adjustVariantsToCurrentBrand(models: ModelModel[] | undefined): Promise<void> {
		models?.map(x => x.DefaultVariantModels = x.DefaultVariantModels.filter(v => v.Brand === this.brandModeService.currentBrandValue()));
	}

	public async getModels(user: UserDataModel | null, modelGroup: ModelGroupModel | undefined | null, modelUuid: string | null = null, defaultModel: boolean = false): Promise<Array<ModelModel | null> | undefined> {
		let result = await this.storeService.getOrSetFromStore<Array<ModelModel>>(environment.dataUrl + this.modelDataUrl + modelGroup?.modelGroupDataUuid);

		this.adjustVariantsToCurrentBrand(result);

		if (this.enduserDisplayService.IsDisplay() && modelGroup?.IsRimless) {
			result?.forEach(model => {
				model.ModelSizeCodeModels = model.ModelSizeCodeModels.filter(x => x.Default);
			});
		}

		if (this.brandModeService.isPrecious) {
			result = result?.filter(model => model.Brand == BrandEnum.Precious || model.Brand == BrandEnum.TitaniumAndPrecious);
		}
		else {
			result = result?.filter(model => model.Brand == BrandEnum.Titanium || model.Brand == BrandEnum.TitaniumAndPrecious);
		}

		if (modelUuid) {
			result = result?.filter(model => model.Uuid == modelUuid);
		}
		else if (defaultModel) {
			result = result?.sort((a, b) => b.Order.localeCompare(a.Order));
			if (result)
				result.length = 1;
		}
		const isAdmin = user?.UserPermissions?.some(x => x == PermissionConstants.IsAdmin) ?? false;
		if (this.userService.adminModeEnabled && isAdmin) {
			const allManualDefaultsData: ManualDefaultModel[] = (await this.httpService.getDataWithOfflineCheck<Array<ManualDefaultModel>>(`${environment.dataUrl}/ModelGroup/${modelGroup?.modelGroupId}/ManualDefaults/${this.brandModeService.currentBrandValue()}`));
			const allManualDefaults = allManualDefaultsData.map((x) => plainToClass(ManualDefaultModel, x));
			if (result)
				for (let x of result) {
					const manualDefault = allManualDefaults.find(y => y.ModelId == x.Id && y.Brand === this.brandModeService.currentBrandValue());

					if (manualDefault) {
						x = await this.overwriteWithManualDefaults(x, modelGroup, manualDefault);
					}
				}
		}

		if (this.isUserInAdminMode && isAdmin) {
			const allManualDefaults = (await this.httpService.getDataWithOfflineCheck<Array<ManualDefaultModel>>(`${environment.dataUrl}/ModelGroup/${modelGroup?.modelGroupId}/ManualDefaults/${this.brandModeService.currentBrandValue()}`)).map((x: ManualDefaultModel) => plainToClass(ManualDefaultModel, x));
			if (result)
				for (let x of result) {
					const manualDefault = allManualDefaults.find((y: ManualDefaultModel) => y.ModelId == x.Id && y.Brand === this.brandModeService.currentBrandValue());
					if (manualDefault) {
						x = await this.overwriteWithManualDefaults(x, modelGroup, manualDefault);
					}
				}
		}

		const mapResult = result?.map((x) => ModelModel.createCopy(x)).sort((a, b) => {
			if (a && a.Order && b && b.Order) {
				return b.Order.localeCompare(a.Order);
			}

			return 0;
		});

		return mapResult;
	}

	private async overwriteWithManualDefaults(model: ModelModel, modelgroup: ModelGroupModel | undefined | null, manualDefault: ManualDefaultModel): Promise<ModelModel> {
		model.DefaultModelConfigurationCode = manualDefault.ModelConfigCode;
		model.DefaultModelSizeId = manualDefault.ModelSizeId;
		const allVariants = await this.getVariants(manualDefault.ModelConfigUuid, modelgroup);

		let defaults = new Array<ComponentVariantModel>();

		manualDefault.ManualDefaultVariants.forEach(defaultVariant => {
			const variant = allVariants?.find(x => x.Id == defaultVariant.ComponentVariantId);

			const vari = variant?.ToComponentVariantModel("", '', false);

			const size = variant?.VariantSizes.find(x => x.Id == defaultVariant.ComponentSizeId);
			if (vari) {
				vari.VariantSize = size;
				const color = size?.VariantColors.find(x => x.Color?.Id == defaultVariant.ColorId);
				vari.VariantColor = color;
				defaults.push(vari);
			}
		});

		// to accompany optionals because we arent setting them currently
		defaults = defaults.concat(model.DefaultVariantModels.filter(x => x.Optional));

		model.DefaultVariantModels = defaults;

		return model;
	}

	private async getVariants(modelconfigUuid: string, modelgroup?: ModelGroupModel | null, componentUuid?: string, componentVariantUuid?: string, componentSizeUuid?: string): Promise<Array<ComponentVariantFromApiModel> | undefined> {
		return this.getVariantsForModel(modelconfigUuid, modelgroup, componentUuid, componentVariantUuid, componentSizeUuid, this.enduserDisplayService.IsDisplay());
	}

	private async getVariantsForModel(modelconfigUuid: string, modelgroup: ModelGroupModel | undefined | null, componentUuid: string | undefined, componentVariantUuid: string | undefined, componentSizeUuid: string | undefined, isDisplay: boolean): Promise<Array<ComponentVariantFromApiModel> | undefined> {
		const url = (environment.dataUrl) + this.variantDataUrl + `requestModel.configurationUuid=${modelconfigUuid}&requestModel.ModelgroupUuid=${modelgroup?.modelGroupDataUuid}`;
		let variants = await this.storeService.getOrSetFromStore<Array<ComponentVariantFromApiModel>>(url);

		if (componentUuid) {
			variants = variants?.filter(x => x.Component.Uuid == componentUuid);
		}

		if (componentVariantUuid) {
			variants = variants?.filter(x => x.Uuid == componentVariantUuid);
		}

		if (componentSizeUuid) {
			variants?.forEach(x => x.VariantSizes = x.VariantSizes.filter(size => size.Uuid == componentSizeUuid));
		}

		if (this.brandModeService.isPrecious) {
			variants = variants?.filter(model => model.Brand == BrandEnum.Precious || model.Brand == BrandEnum.TitaniumAndPrecious);
		}
		else {
			variants = variants?.filter(model => model.Brand == BrandEnum.Titanium || model.Brand == BrandEnum.TitaniumAndPrecious);
		}

		if (isDisplay) {
			variants = variants?.filter(x => x.AvailableForDisplay == isDisplay);

			variants?.forEach(variant => {
				variant.VariantSizes = variant.VariantSizes.filter(x => x.AvailableForDisplay == isDisplay);

				variant.VariantSizes.forEach(size => {
					size.VariantColors = size.VariantColors.filter(x => x.AvailableForDisplay == isDisplay);
				});
			});
		}

		const result = variants?.map((x) => ComponentVariantFromApiFactory.createCopy(x));

		return result;
	}

	public async getVariantsOnModelgroup(modelgroup: ModelGroupModel): Promise<Array<ComponentVariantFromApiModel> | undefined> {
		const result = await this.storeService.getOrSetFromStore<Array<ComponentVariantFromApiModel>>(`${environment.dataUrl}/componentvariantdistinct?requestModel.modelgroupUuid=${modelgroup.modelGroupDataUuid}`);

		return result?.map((x) => ComponentVariantFromApiFactory.createCopy(x));
	}

	public async getCaseMiscModel(caseItemNo: string, caseItemVariantCode: string) {
		return await this.miscModelsService.getCaseMiscModel(caseItemNo, caseItemVariantCode);
	}

	public async getModelGroup(uuid?: string): Promise<ModelGroupModel | null> {
		// const result = await (this.storeService.getOrSetFromStore<Array<ModelGroupModel>>(`${environment.dataUrl}/modelgroups`));
		const result = await this.modelGroupStore.modelGroups();

		const resultModelgroup = result?.find(x => x.Uuid.toLowerCase() == uuid?.toLowerCase());
		return ModelGroupModel.createCopy(resultModelgroup as ModelGroupModel);
	}

	public async getModelGroupFromName(name: string | null): Promise<ModelGroupModel | null> {
		// const result = await (this.storeService.getOrSetFromStore<Array<ModelGroupModel>>(`${environment.dataUrl}/modelgroups`));
		const result = await this.modelGroupStore.modelGroups();
		const modelGroup = result?.find(x => x.ShortDescription?.Fallback?.toLowerCase() === name?.toLowerCase() || x.Description?.Fallback?.toLowerCase() === name?.toLowerCase() || x.ProductLineCode?.toLowerCase() === name?.toLowerCase());
		return ModelGroupModel.createCopy(modelGroup as ModelGroupModel);
	}

	public async getModelGroupsFromName(name: string | null): Promise<ModelGroupModel[]> {
		const allGroups = await this.modelGroupStore.modelGroups();
		
		const lowerCaseName = (name ?? "").toLowerCase();
		const groupsFromName = allGroups?.filter(group => group.ShortDescription?.Fallback?.toLowerCase() === lowerCaseName || group.Description?.Fallback?.toLowerCase() === lowerCaseName || group.ProductLineCode?.toLowerCase() === lowerCaseName);

		return groupsFromName?.map(x => ModelGroupModel.createCopy(x)).filter(x => x != null) ?? [];
	}

	public async getModelGroupFromProductLineCode(code: string): Promise<ModelGroupModel | null> {
		// const result = await (this.storeService.getOrSetFromStore<Array<ModelGroupModel>>(`${environment.dataUrl}/modelgroups`));
		const result = await this.modelGroupStore.modelGroups();
		const modelGroup = result?.find(x => x.ProductLineCode === code);
		return ModelGroupModel.createCopy(modelGroup as ModelGroupModel);
	}

	public async getDefaultModel(user: UserDataModel | null, modelGroupModel: ModelGroupModel): Promise<ModelModel> {
		const result = await this.getModels(user, modelGroupModel, null, true);
		return plainToClass(ModelModel, result?.[0]);
	}

	public async getModelSizeMinMax(): Promise<ModelSizeMinMaxModel> {
		const result = await this.storeService.getOrSetFromStore<ModelModel>(`${environment.dataUrl}/modelsize/minmax`);
		return plainToClass(ModelSizeMinMaxModel, result);
	}

	public async getAllModelGroups(): Promise<Array<ModelGroupModel | null> | undefined> {
		// const result = await (this.storeService.getOrSetFromStore<Array<ModelGroupModel>>(`${environment.dataUrl}/modelgroups`));
		const result = await this.modelGroupStore.modelGroups();
		return result?.map((x) => ModelGroupModel.createCopy(x as ModelGroupModel));
	}

	public async getKidsModelGroups(): Promise<Array<ModelGroupModel | null> | undefined> {
		// const result = await (this.storeService.getOrSetFromStore<Array<ModelGroupModel>>(`${environment.dataUrl}/modelgroups`));
		let result = await this.modelGroupStore.modelGroups();
		result = result?.filter(x => x.KidTeen == true);
		return result?.map((x) => ModelGroupModel.createCopy(x as ModelGroupModel));
	}

	public async getAdultModelGroups(): Promise<Array<ModelGroupModel | null> | undefined> {
		// const result = await (this.storeService.getOrSetFromStore<Array<ModelGroupModel>>(`${environment.dataUrl}/modelgroups`));
		let result = await this.modelGroupStore.modelGroups();
		result = result?.filter(x => x.KidTeen == false && x.ParentModelGroupUuid == null);

		return result?.map((x) => ModelGroupModel.createCopy(x as ModelGroupModel));
	}

	public async getModelFromName(user: UserDataModel | null, modelGroups: ModelGroupModel[], name: string | null): Promise<ModelModel | undefined | null> {
		for (const group of modelGroups) {
			const models = await this.getModels(user, group);
			const model = models?.find(model => model?.Code == name);
			if (model) {
				return model;
			}
		}

		return null;
	}

	public async getModel(user: UserDataModel | null, modelGroup?: ModelGroupModel | null, modelUuid?: string): Promise<ModelModel | undefined | null> {
		const result = await this.getModels(user, modelGroup, modelUuid);
		return result?.find(x => x?.Uuid == modelUuid);
	}

	public async getModelsWithAllDataRequest(modelGroup: ModelGroupModel): Promise<Observable<HttpEvent<ModelReponseWithVariantsModelFromApi>>> {
		const req = new HttpRequest('GET', `${environment.dataUrl}/allModelData/${modelGroup.Uuid}`, {
			reportProgress: true
		});

		return this.http.request<ModelReponseWithVariantsModelFromApi>(req);
	}

	public async setModelsWithAllData(modelGroup: ModelGroupModel, model: ModelReponseWithVariantsModelFromApi) {
		await this.storeService.getOrSetFromStore<Array<ModelModel>>((environment.dataUrl) + this.modelDataUrl + modelGroup.modelGroupDataUuid, model.ModelReponseModels);

		this.adjustVariantsToCurrentBrand(model.ModelReponseModels);

		const groupedVariants = model.ComponentVariantModels.reduce((r, a) => {
			r[a.ModelConfigUuid] = r[a.ModelConfigUuid] || [];
			r[a.ModelConfigUuid].push(a);
			return r;
		}, Object.create(null));

		for (const modelconfigUuid in groupedVariants) {
			const variants = groupedVariants[modelconfigUuid];
			const url = (environment.dataUrl) + this.variantDataUrl + `requestModel.configurationUuid=${modelconfigUuid}&requestModel.ModelgroupUuid=${modelGroup.modelGroupDataUuid}`;
			await this.storeService.getOrSetFromStore<Array<ComponentVariantFromApiModel>>(url, variants);
		}

		await this.storeService.getOrSetFromStore<string>((environment.dataUrl) + this.modelWithAllDataUrl + modelGroup.Uuid, "saved");
	}

	public async getMiscModels(includeItemsWithZeroMaxQuantity: boolean = false): Promise<Array<MiscModel>> {
		return await this.miscModelsService.getMiscModels(includeItemsWithZeroMaxQuantity);
	}

	public async loadAllModelgroups(): Promise<Array<ModelGroupModel | null>> {
		try {
			const modelgroups: (ModelGroupModel | null)[] = await this.getAdultModelGroups() ?? [];
			const modelgroupsKids: (ModelGroupModel | null)[] = await this.getKidsModelGroups() ?? [];

			const allModelgroups = modelgroups.concat(modelgroupsKids);
			return allModelgroups;
		} catch (error) {
			throw error;
		}
	}

	public async searchModels(search?: string, tags?: Array<TagModel>, hboxFrom?: number | null, hboxTo?: number | null): Promise<Array<ModelModel | null> | undefined> {
		let tagUrl = `&requestModel.hboxFrom=${hboxFrom || ""}&requestModel.hboxTo=${hboxTo || ""}`;

		tags = tags || [];
		tags.forEach(tag => {
			tagUrl += "&requestModel.tags=" + tag.Id;
		});

		const result = await this.storeService.getOrSetFromStore<Array<ModelModel>>(`${environment.dataUrl}/models/search?requestModel.searchCriteria=${search || ""}${tagUrl}`);
		return result?.map((x) => ModelModel.createCopy(x));
	}

	public async getAllProductLines(): Promise<Array<ProductLineModel>> {
		let url = `${environment.dataUrl}/v2/productlines`;
		const pls = await this.httpService.getDataWithOfflineCheck<Array<ProductLineModel>>(url);
		return pls;
	}
}

export class ModelsResponse {

	Id: number;
	ModelLineId: number;
	Name: string;
	Release: Date;
	Discontinuation?: Date;
	DefaultBoxSize: string;
	ImageUrl: string;

}
